import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { cn } from '../cn';
import { Arrows, General } from '../StrokeIcons';
import type { ColorName, ColorVariant } from './buttonClasses';
import {
  composeVariant,
  popoverBorderClasses as popoverBorder,
  popoverHighlightClasses as popoverHighlight,
  popoverTextClasses as popoverText,
} from './buttonClasses';
import type { Style } from './colors';
import Input from './Input';
import type { Size } from './types';
import {
  sizeFont,
  sizeHeight,
  sizeIconClass,
  sizeInnerGap,
  sizePaddingL,
  sizePaddingR,
  sizeRounded,
} from './types';

export type FilterPopoverItem = {
  value: string;
  label: string;
  icon?: ReactNode;
  /** Secondary text shown on a second line in a muted style. */
  description?: string;
  /** Per-item highlight color override. Falls back to the popover-level color. */
  color?: ColorName;
  /** Per-item highlight tone override. Defaults to `'filled'` when `color` is set. */
  tone?: Style;
  /** Optional action button rendered on the right side of the option. */
  action?: {
    icon: ReactNode;
    onClick: (value: string, e: React.MouseEvent) => void;
    className?: string;
    /** What the button does, for screen readers and as its hover title — it is icon-only. */
    label?: string;
    /** Close the popover after the click — for an action that opens something over the page (a modal). */
    closes?: boolean;
  };
  /** Optional keyboard shortcut (1-9). Displayed as badge and triggers selection on keypress. */
  shortcut?: number;
  /**
   * Opens a deeper list instead of closing (FilterSelect only): selecting it still calls `onChange`,
   * but the popover stays open with its filter cleared, so the caller can swap `options` for the
   * next level — a "back" row is a drill row too. After a drill the cursor lands on the first
   * non-drill row of the new list.
   */
  drill?: boolean;
};

/** A group of items with a non-selectable header label. */
export type FilterPopoverItemGroup = {
  label: string;
  items: FilterPopoverItem[];
};

/** Type guard to check if an item is a group header. */
function isItemGroup(item: FilterPopoverItem | FilterPopoverItemGroup): item is FilterPopoverItemGroup {
  return 'items' in item && Array.isArray((item as FilterPopoverItemGroup).items);
}

export type FilterPopoverProps = {
  popoverId: string;
  anchorName: string;
  size?: Size;
  color?: ColorName;
  tone?: Style;
  /** Items can be flat or include groups with headers. */
  items: (FilterPopoverItem | FilterPopoverItemGroup)[];
  selectedValue?: string;
  /** When set, the popover enters multi-select mode: every value in this list is rendered as selected, clicking a row toggles via `onSelect` without closing the popover. */
  multiSelectedValues?: string[];
  onSelect: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  /** Additional inline styles merged with base positioning (e.g., width/minWidth). */
  popoverStyle?: React.CSSProperties;
  /** Override icon wrapper class. Default: sizeIconClass(size) + [&>*]:size-full. Pass empty string to skip sizing. */
  iconClassName?: string;
  /** Show the filter/search input. Default true. Set false for short lists. */
  filterable?: boolean;
  /** Called when filter input changes. Use for async search with debouncing in parent. */
  onFilterInputChange?: (query: string) => void;
  /** Show loading indicator when fetching async options. */
  isLoadingOptions?: boolean;
  /** Icon to show in the filter input (typically the same icon as the trigger button). */
  inputIcon?: ReactNode;
  /** Called when user wants to create a new item from the filter query. Shows a "Create X" row. */
  onCreate?: (value: string) => void;
  /** Icon for the create row. Defaults to General.PlusSquare. */
  createIcon?: ReactNode;
};

export default function FilterPopover({
  popoverId,
  anchorName,
  size = 'md',
  color,
  tone,
  items,
  selectedValue,
  multiSelectedValues,
  onSelect,
  onOpenChange,
  popoverStyle,
  iconClassName,
  filterable = true,
  onFilterInputChange,
  isLoadingOptions,
  inputIcon,
  onCreate,
  createIcon,
}: FilterPopoverProps) {
  const variant: ColorVariant = composeVariant(color ?? 'gray', tone ?? 'filled');
  /** Per-item highlight: an item's own color wins, otherwise the popover-level color. */
  const itemVariant = (item: FilterPopoverItem): ColorVariant =>
    item.color != null ? composeVariant(item.color, item.tone ?? 'filled') : variant;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const effectiveIconCls = iconClassName ?? cn(sizeIconClass(size), 'overflow-hidden *:size-full [&_img]:size-full [&_img]:object-cover');

  // Filter items while preserving group structure
  const filtered = useMemo((): (FilterPopoverItem | FilterPopoverItemGroup)[] => {
    if (!query) return items;
    const q = query.toLowerCase();
    const result: (FilterPopoverItem | FilterPopoverItemGroup)[] = [];
    for (const item of items) {
      if (isItemGroup(item)) {
        // If query matches group header, include all items in the group
        if (item.label.toLowerCase().includes(q)) {
          result.push(item);
        } else {
          // Otherwise filter individual items
          const filteredGroupItems = item.items.filter(
            (o) => o.label.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q),
          );
          // Only include group if it has matching items
          if (filteredGroupItems.length > 0) {
            result.push({ ...item, items: filteredGroupItems });
          }
        }
      } else {
        // Regular item - filter normally
        const matches = item.label.toLowerCase().includes(q) || item.description?.toLowerCase().includes(q);
        if (matches) result.push(item);
      }
    }
    return result;
  }, [items, query]);

  // Flatten filtered items for keyboard navigation indexing (excluding collapsed groups)
  const filteredFlat = useMemo((): FilterPopoverItem[] => {
    return filtered.flatMap((item) => {
      if (isItemGroup(item)) {
        // Don't include items from collapsed groups in keyboard navigation
        if (collapsedGroups.has(item.label)) return [];
        return item.items;
      }
      return [item];
    });
  }, [filtered, collapsedGroups]);

  // Show create row when onCreate is provided and there's a trimmed query
  const trimmedQuery = query.trim();
  const showCreateRow = !!onCreate && trimmedQuery.length > 0;
  // Index for the create row (after all filtered items)
  const createRowIndex = filteredFlat.length;
  // Total navigable items including the create row
  const totalNavigableItems = filteredFlat.length + (showCreateRow ? 1 : 0);

  const toggleGroup = (label: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const isMultiSelect = multiSelectedValues != null;
  const multiSelectedSet = useMemo(() => new Set(multiSelectedValues ?? []), [multiSelectedValues]);

  // Set by a drill: the caller swaps `items` for the next level, and once it has, the cursor goes
  // to the first real choice rather than a navigation row (the "back" row usually leads the list).
  const [drillPending, setDrillPending] = useState(false);
  useEffect(() => {
    if (!drillPending) return;
    setDrillPending(false);
    const flat = items.flatMap((item) => (isItemGroup(item) ? item.items : [item]));
    const first = flat.findIndex((item) => item.drill !== true);
    setHighlightedIndex(first >= 0 ? first : 0);
    if (listRef.current != null) listRef.current.scrollTop = 0;
  }, [items, drillPending]);

  function selectItem(item: FilterPopoverItem) {
    onSelect(item.value);
    if (item.drill === true) {
      // Stay open on a fresh, unfiltered list; the input keeps focus for the next search.
      setQuery('');
      onFilterInputChange?.('');
      setHighlightedIndex(0);
      setDrillPending(true);
      return;
    }
    if (!isMultiSelect) popoverRef.current?.hidePopover();
  }

  function scrollToIndex(index: number) {
    const el = listRef.current?.querySelector(`[data-option-index="${index}"]`) as HTMLElement | undefined;
    if (!el || !listRef.current) return;

    const container = listRef.current;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Account for sticky headers (~28px height)
    const stickyHeaderOffset = 28;

    // Calculate how much the element is hidden
    const hiddenAbove = (containerRect.top + stickyHeaderOffset) - elRect.top;
    const hiddenBelow = elRect.bottom - containerRect.bottom;

    if (hiddenAbove > 0) {
      // Element is partially/fully above visible area - scroll up by the amount hidden
      container.scrollTop -= hiddenAbove;
    } else if (hiddenBelow > 0) {
      // Element is partially/fully below visible area - scroll down by the amount hidden
      container.scrollTop += hiddenBelow;
    }
  }

  // Scroll to highlighted element when filter changes
  useEffect(() => {
    if (isOpen && filteredFlat.length > 0) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => scrollToIndex(highlightedIndex));
    }
  }, [query, filteredFlat.length]);

  function handleCreate() {
    onCreate?.(trimmedQuery);
    popoverRef.current?.hidePopover();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = highlightedIndex < totalNavigableItems - 1 ? highlightedIndex + 1 : 0;
      setHighlightedIndex(next);
      scrollToIndex(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const next = highlightedIndex > 0 ? highlightedIndex - 1 : totalNavigableItems - 1;
      setHighlightedIndex(next);
      scrollToIndex(next);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation(); // Prevent bubbling to parent div which also has handleKeyDown
      if (highlightedIndex === createRowIndex && showCreateRow) {
        handleCreate();
      } else {
        const opt = filteredFlat[highlightedIndex];
        if (opt) selectItem(opt);
      }
    } else if (e.key >= '1' && e.key <= '9') {
      // Numeric shortcuts: find item with matching shortcut property
      const keyNum = parseInt(e.key, 10);
      const opt = filteredFlat.find((item) => item.shortcut === keyNum);
      if (opt) {
        e.preventDefault();
        selectItem(opt);
      }
    }
  }

  return (
    <div
      ref={popoverRef}
      id={popoverId}
      popover="auto"
      onKeyDown={handleKeyDown}
      onToggle={(e) => {
        const open = (e as React.ToggleEvent<HTMLDivElement>).newState === 'open';
        setIsOpen(open);
        onOpenChange?.(open);
        if (open) {
          setQuery('');
          // Find the index of the selected item, default to 0 if not found
          const flatItems = items.flatMap((item) => (isItemGroup(item) ? item.items : [item]));
          const selectedIndex = flatItems.findIndex((item) => item.value === selectedValue);
          const initialIndex = selectedIndex >= 0 ? selectedIndex : 0;
          setHighlightedIndex(initialIndex);
          requestAnimationFrame(() => {
            if (filterable) inputRef.current?.focus();
            else listRef.current?.focus();
            // Scroll to the selected item
            scrollToIndex(initialIndex);
          });
        }
      }}
      style={{
        position: 'fixed',
        positionAnchor: anchorName,
        positionArea: 'block-end span-inline-end',
        positionTryFallbacks: 'flip-block, flip-inline, flip-block flip-inline',
        justifySelf: 'start',
        margin: 0,
        inset: 'auto',
        ...popoverStyle,
      } as unknown as React.CSSProperties}
      className={cn(
        'p-0 border bg-white dark:bg-charade-950 shadow-lg',
        'max-w-[calc(100vw-1rem)]',
        popoverBorder(variant),
        popoverText(variant),
        sizeRounded(size),
        sizeFont(size),
        !isOpen && 'hidden',
      )}>
      {/* Filter input */}
      {filterable && (
        <Input
          ref={inputRef}
          size={size}
          color={color}
          tone={tone}
          shape="square"
          bordered={false}
          icon={inputIcon}
          value={query}
          onChange={(e) => {
            const val = e.target.value;
            setQuery(val);
            onFilterInputChange?.(val);
            setHighlightedIndex(0);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Filter..."
          className="w-full"
        />
      )}

      {/* Option list */}
      <div ref={listRef} role="listbox" tabIndex={filterable ? undefined : -1} className={cn('border-t', popoverBorder(variant), 'max-h-[40vh] overflow-auto outline-hidden')}>
        {isLoadingOptions && (
          <div className={cn('text-slate-500 dark:text-slate-300 px-3', sizeHeight(size), 'flex items-center')}>
            Loading...
          </div>
        )}
        {!isLoadingOptions && filteredFlat.length === 0 && !showCreateRow && (
          <div className={cn('text-slate-500 dark:text-slate-300 px-3', sizeHeight(size), 'flex items-center')}>
            No results
          </div>
        )}
        {filtered.map((item, idx) => {
          if (isItemGroup(item)) {
            // Calculate flat index for items in this group (accounting for collapsed groups)
            const prevItems = filtered.slice(0, idx);
            const groupStartIndex = prevItems.reduce(
              (acc, prev) => {
                if (isItemGroup(prev)) {
                  return acc + (collapsedGroups.has(prev.label) ? 0 : prev.items.length);
                }
                return acc + 1;
              },
              0,
            );
            const isCollapsed = collapsedGroups.has(item.label);
            return (
              <div key={`group-${item.label}`}>
                {/* Group Header - Clickable to collapse/expand */}
                <div
                  onClick={() => toggleGroup(item.label)}
                  className={cn(
                    'group/header px-3 py-1.5 text-xs font-bold uppercase text-slate-700 dark:text-slate-200',
                    'bg-slate-100 dark:bg-charade-800 sticky top-0 z-10',
                    'cursor-pointer hover:bg-slate-200 dark:hover:bg-charade-700 transition-colors',
                    'flex items-center justify-between gap-2',
                  )}>
                  <span>{item.label}</span>
                  <Arrows.ChevronDown
                    className={cn(
                      'size-3 transition-all opacity-50 group-hover/header:opacity-100',
                      isCollapsed && '-rotate-90',
                    )}
                  />
                </div>
                {/* Group Items - Only show if not collapsed */}
                {!isCollapsed && item.items.map((opt, groupItemIndex) => {
                  const itemFlatIndex = groupStartIndex + groupItemIndex;
                  const isMultiChecked = isMultiSelect && multiSelectedSet.has(opt.value);
                  return (
                    <div
                      key={opt.value}
                      role="option"
                      data-option-index={itemFlatIndex}
                      aria-selected={isMultiSelect ? isMultiChecked : opt.value === selectedValue}
                      onMouseEnter={() => setHighlightedIndex(itemFlatIndex)}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        selectItem(opt);
                      }}
                      className={cn(
                        'flex items-center cursor-pointer',
                        sizePaddingL(size),
                        sizePaddingR(size),
                        opt.description ? 'py-1' : sizeHeight(size),
                        sizeInnerGap(size),
                        itemFlatIndex === highlightedIndex && popoverHighlight(itemVariant(opt)),
                        itemFlatIndex !== highlightedIndex && itemFlatIndex % 2 === 1 && 'bg-slate-50 dark:bg-charade-900',
                        itemFlatIndex !== highlightedIndex && 'hover:bg-slate-100 dark:hover:bg-charade-800',
                        opt.value === selectedValue && itemFlatIndex !== highlightedIndex && 'font-medium',
                        isMultiChecked && itemFlatIndex !== highlightedIndex && 'font-medium',
                      )}>
                      {opt.icon && (
                        <span className={cn('shrink-0 flex items-center justify-center', effectiveIconCls)}>
                          {opt.icon}
                        </span>
                      )}
                      <span className="min-w-0 flex flex-col flex-1">
                        <span className="truncate">{opt.label}</span>
                        {opt.description && (
                          <span className={cn('line-clamp-2 text-xs', itemFlatIndex === highlightedIndex ? 'text-white/70' : 'text-slate-500 dark:text-slate-300')}>{opt.description}</span>
                        )}
                      </span>
                      {isMultiChecked && (
                        <General.Check className={cn('shrink-0 size-4', itemFlatIndex === highlightedIndex ? 'text-current' : 'text-slate-700 dark:text-slate-200')} />
                      )}
                      {opt.action && (
                        <button
                          type="button"
                          aria-label={opt.action.label}
                          title={opt.action.label}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            opt.action!.onClick(opt.value, e);
                            if (opt.action!.closes === true) popoverRef.current?.hidePopover();
                          }}
                          className={cn('shrink-0 flex items-center justify-center', opt.action.className)}>
                          {opt.action.icon}
                        </button>
                      )}
                      {opt.shortcut && (
                        <span
                          className={cn(
                            'shrink-0 flex items-center justify-center text-[10px] font-medium rounded-sm size-4',
                            itemFlatIndex === highlightedIndex
                              ? 'bg-white/20 text-current'
                              : 'bg-slate-200 text-slate-700 dark:bg-charade-700 dark:text-slate-200',
                          )}>
                          {opt.shortcut}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          }
          // Regular (ungrouped) item - calculate its flat index
          const prevItems = filtered.slice(0, idx);
          const itemFlatIndex = prevItems.reduce(
            (acc, prev) => acc + (isItemGroup(prev) ? prev.items.length : 1),
            0,
          );
          const isMultiChecked = isMultiSelect && multiSelectedSet.has(item.value);
          return (
            <div
              key={item.value}
              role="option"
              data-option-index={itemFlatIndex}
              aria-selected={isMultiSelect ? isMultiChecked : item.value === selectedValue}
              onMouseEnter={() => setHighlightedIndex(itemFlatIndex)}
              onMouseDown={(e) => {
                e.preventDefault();
                selectItem(item);
              }}
              className={cn(
                'flex items-center cursor-pointer',
                sizePaddingL(size),
                sizePaddingR(size),
                item.description ? 'py-1' : sizeHeight(size),
                sizeInnerGap(size),
                itemFlatIndex === highlightedIndex && popoverHighlight(itemVariant(item)),
                itemFlatIndex !== highlightedIndex && itemFlatIndex % 2 === 1 && 'bg-slate-50 dark:bg-charade-900',
                itemFlatIndex !== highlightedIndex && 'hover:bg-slate-100 dark:hover:bg-charade-800',
                item.value === selectedValue && itemFlatIndex !== highlightedIndex && 'font-medium',
                isMultiChecked && itemFlatIndex !== highlightedIndex && 'font-medium',
              )}>
              {item.icon && (
                <span className={cn('shrink-0 flex items-center justify-center', effectiveIconCls)}>
                  {item.icon}
                </span>
              )}
              <span className="min-w-0 flex flex-col flex-1">
                <span className="truncate">{item.label}</span>
                {item.description && (
                  <span className={cn('line-clamp-2 text-xs', itemFlatIndex === highlightedIndex ? 'text-white/70' : 'text-slate-500 dark:text-slate-300')}>{item.description}</span>
                )}
              </span>
              {isMultiChecked && (
                <General.Check className={cn('shrink-0 size-4', itemFlatIndex === highlightedIndex ? 'text-current' : 'text-slate-700 dark:text-slate-200')} />
              )}
              {item.action && (
                <button
                  type="button"
                  aria-label={item.action.label}
                  title={item.action.label}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    item.action!.onClick(item.value, e);
                    if (item.action!.closes === true) popoverRef.current?.hidePopover();
                  }}
                  className={cn('shrink-0 flex items-center justify-center', item.action.className)}>
                  {item.action.icon}
                </button>
              )}
              {item.shortcut && (
                <span
                  className={cn(
                    'shrink-0 flex items-center justify-center text-[10px] font-medium rounded-sm size-4',
                    itemFlatIndex === highlightedIndex
                      ? 'bg-white/20 text-current'
                      : 'bg-slate-200 text-slate-700 dark:bg-charade-700 dark:text-slate-200',
                  )}>
                  {item.shortcut}
                </span>
              )}
            </div>
          );
        })}
        {/* Create row */}
        {showCreateRow && (
          <div
            role="option"
            data-option-index={createRowIndex}
            onMouseEnter={() => setHighlightedIndex(createRowIndex)}
            onMouseDown={(e) => {
              e.preventDefault();
              handleCreate();
            }}
            className={cn(
              'flex items-center cursor-pointer',
              sizePaddingL(size),
              sizePaddingR(size),
              sizeHeight(size),
              sizeInnerGap(size),
              highlightedIndex === createRowIndex && popoverHighlight(variant),
              highlightedIndex !== createRowIndex && 'hover:bg-slate-100 dark:hover:bg-charade-800',
            )}>
            <span className={cn('shrink-0 flex items-center justify-center', sizeIconClass(size), '*:size-full')}>
              {createIcon ?? <General.PlusSquare />}
            </span>
            <span className="truncate">
              Create "<span className="font-medium">{trimmedQuery}</span>"
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
